/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { MdDiscount } from 'react-icons/md';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import DiscountCodeKindButton from './DiscountCodeKindButton';
import TierSelectionField from './TierSelectionField';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import DateTimeRangePicker from 'legacy/components/util/FormHelpers/DateTimeRangePicker';
import { useGetEvent } from '../../hooks';
import dayjs from 'dayjs';
import Field from 'legacy/common/components/wysiwyg-form/Field';
import { discountCode as discountCodeRegexp } from '../../../util/validations/regexp';

const TIME_FORMAT = 'h:mm A';

type DiscountCodeFormProps = {
  eventId: string;
  title?: string;
  initialValues?: any;
  errorMessage?: string;
  onClose: () => void;
  onDelete?: (id) => void;
  onSubmit: ({
    eventId,
    code,
    discount
  }: {
    id?: string;
    eventId: string;
    code: string;
    discount: { value: number; kind: string };
    startDate?: Date;
    endDate?: Date;
    maxUsage?: number;
    isActive: boolean;
    tierIds?: string[];
  }) => void;
};

const DiscountCodeForm: React.FC<DiscountCodeFormProps> = ({
  eventId,
  errorMessage = '',
  initialValues,
  title,
  onClose,
  onSubmit,
  onDelete
}) => {
  const { t } = useTranslation('events');
  const flags = useFlags();
  const { data: event, isLoading } = useGetEvent(eventId);

  const DiscountCodeSchema = useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string()
          .matches(
            discountCodeRegexp,
            t('settings.discountCodes.page.validation.invalidCode')
          )
          .required(t('settings.discountCodes.page.validation.codeRequired'))
          .max(20, t('settings.discountCodes.page.validation.codeMaxLength')),
        amount: Yup.number()
          .required(
            t('settings.discountCodes.page.validation.discountRequired')
          )
          .min(0, t('settings.discountCodes.page.validation.discountMinValue'))
      }),
    [t]
  );

  const maxEndDate = useMemo(() => {
    if (isLoading || !event) {
      return null;
    }

    const effectiveEndDate = new Date(event.startDate);
    effectiveEndDate.setDate(effectiveEndDate.getDate() + 1);

    return event.endDate ? event.endDate : effectiveEndDate.toISOString();
  }, [event, isLoading]);

  return (
    <Formik
      initialValues={{
        code: initialValues?.code || '',
        amount: initialValues?.discount.value || undefined,
        kind: initialValues?.discount.kind || undefined,
        startDate: initialValues?.startDate || undefined,
        endDate: initialValues?.endDate || undefined,
        startDateTime: initialValues?.startDate
          ? dayjs(initialValues?.startDate).format(TIME_FORMAT)
          : undefined,
        endDateTime: initialValues?.endDate
          ? dayjs(initialValues?.endDate).format(TIME_FORMAT)
          : undefined,
        maxUsage: initialValues?.maxUsage || undefined,
        tierIds: flags[FLAGS.ENABLE_TIERS_DISCOUNT_CODES]
          ? initialValues?.tierIds || []
          : [],
        isActive:
          typeof initialValues?.isActive === 'boolean'
            ? initialValues.isActive
            : true
      }}
      onSubmit={(values) => {
        onSubmit({
          id: initialValues?.id || undefined,
          eventId,
          code: values.code.toUpperCase(),
          discount: {
            value: values.amount,
            kind: typeof values.kind === 'string' ? values.kind : 'percentage'
          },
          startDate: values.startDate,
          endDate: values.endDate,
          maxUsage: values.maxUsage,
          tierIds:
            flags[FLAGS.ENABLE_TIERS_DISCOUNT_CODES] &&
            values.tierIds.length > 0
              ? values.tierIds
              : undefined,
          isActive: values.isActive
        });
      }}
      validationSchema={DiscountCodeSchema}
    >
      {({ values, submitForm, setFieldValue }) => (
        <Modal
          type={ModalType.SMALL}
          visible
          onClose={onClose}
          shouldStopModalClose={isLoading}
          title={title || t('settings.discountCodes.page.formTitle')}
          icon={<MdDiscount size={22} />}
          renderFooter={() => (
            <div className="w-full">
              <div className="text-red-500">{errorMessage}</div>
              <Button loading={isLoading} onClick={submitForm}>
                {t('settings.discountCodes.page.saveButton')}
              </Button>

              {typeof onDelete === 'function' && (
                <div className="mt-4">
                  <Button
                    type={ButtonType.Danger}
                    loading={isLoading}
                    onClick={() => {
                      onDelete(initialValues?.id);
                    }}
                  >
                    {t('settings.discountCodes.page.deleteDiscountCode')}
                  </Button>
                </div>
              )}
            </div>
          )}
        >
          <Form>
            <div className="p-4 pb-52 mt-4 flex flex-col justify-between">
              <p className="mt-2 mb-6 text-sm font-normal">
                {t('settings.discountCodes.page.formDescription')}
              </p>

              <Field
                id="code"
                name="code"
                type="text"
                label={t('settings.discountCodes.page.codeLabel')}
                placeholder={t('settings.discountCodes.page.codePlaceholder')}
                helper={t('settings.discountCodes.page.codeHelper')}
              />

              <div className="flex items-center justify-center gap-4">
                <Field
                  id="kind"
                  name="kind"
                  label={t('settings.discountCodes.page.discountTypeLabel')}
                  render={DiscountCodeKindButton}
                />

                <Field
                  id="amount"
                  name="amount"
                  type="number"
                  label={t('settings.discountCodes.page.discountAmountLabel')}
                  placeholder={
                    values.kind === 'amount'
                      ? t('settings.discountCodes.page.amountPlaceholder')
                      : t('settings.discountCodes.page.percentagePlaceholder')
                  }
                  disabled={isLoading}
                />
              </div>
              <Field
                id="maxUsage"
                name="maxUsage"
                type="number"
                label={t('settings.discountCodes.page.usageLimitLabel')}
                placeholder={t(
                  'settings.discountCodes.page.usageLimitPlaceholder'
                )}
                disabled={isLoading}
              />

              <DateTimeRangePicker
                minStartDate={new Date().toISOString()}
                maxEndDate={maxEndDate}
              />

              {flags[FLAGS.ENABLE_TIERS_DISCOUNT_CODES] && (
                <TierSelectionField
                  eventId={eventId}
                  selectedTierIds={values.tierIds}
                  onChange={(tierIds) => setFieldValue('tierIds', tierIds)}
                  disabled={isLoading}
                />
              )}
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default DiscountCodeForm;
